import { atom, useAtom, useAtomValue } from 'jotai';
import produce from 'immer';
import { useRequest } from 'ahooks';
import {
  querySpuLabel,
  createSpuLabel,
  updateSpuLabel,
  defaultTagResponse,
  deleteSpuLabel,
  getTag,
} from '@knowledge/service/api/spu';

type querySpuLabelParams = Parameters<typeof querySpuLabel>[0];

export const SearchParams = atom<querySpuLabelParams>({
  tagName: '',
  pageInfo: { pageSize: 10, pageNo: 1 },
});

let refreshFn = () => {};

export function useSpuLabelList() {
  const searchParams = useAtomValue(SearchParams);
  const { data, loading, refresh } = useRequest(() => querySpuLabel(searchParams), {
    manual: false,
    refreshDeps: [
      searchParams.tagName,
      searchParams.pageInfo.pageNo,
      searchParams.pageInfo.pageSize,
    ],
  });

  refreshFn = refresh;

  return {
    data: data || defaultTagResponse(),
    loading,
  };
}

export function useSpuLabelAll() {
  const { data, loading } = useRequest(
    () => querySpuLabel({ tagName: '', pageInfo: { pageSize: 99999, pageNo: 1 } }),
    {
      manual: false,
    },
  );
  return {
    data,
    loading,
  };
}

export function useSpuLabelDetail(id: number) {
  const { data, loading, refresh } = useRequest(() => getTag({ tagId: String(id) }), {
    manual: true,
  });
  return {
    data,
    loading,
    refresh,
  };
}

export function useSpuLabelMutate() {
  const [searchParams, setSearchParams] = useAtom(SearchParams);
  const { runAsync: createLabel, loading: createLoading } = useRequest(
    (data) => createSpuLabel(data),
    {
      manual: true,
      onSuccess: () => {
        if (searchParams.pageInfo.pageNo === 1) {
          refreshFn();
        } else {
          setSearchParams(
            produce((draft) => {
              draft.pageInfo.pageNo = 1;
            }),
          );
        }
      },
    },
  );
  const { runAsync: editLabel, loading: editLoading } = useRequest((data) => updateSpuLabel(data), {
    manual: true,
    onSuccess: () => {
      refreshFn();
    },
  });
  const { runAsync: deleteLabel, loading: deleteLoading } = useRequest(
    (data) => deleteSpuLabel(data),
    {
      manual: true,
      onSuccess: () => {
        refreshFn();
      },
    },
  );

  return {
    createLabel,
    editLabel,
    deleteLabel,
    loading: createLoading || editLoading || deleteLoading,
  };
}
